import { Link } from 'react-router-dom'

export type BatchImportStatus =
  | 'PREPARING'
  | 'UPLOADED'
  | 'VALIDATED'
  | 'VALIDATED_WITH_ERRORS'
  | 'INVALID'
  | 'IMPORTED'
  | 'IMPORTED_WITH_ERRORS'
  | 'EANSEARCH'
  | 'EANSEARCH_WITH_ERRORS'
  | 'DONE'
  | 'DONE_W_SMARTMATCH'
  | 'DONE_W_SMARTMATCH_NOMATCH'
  | 'DONE_WITH_ERRORS'
  | 'DONE_WITH_CAVEATS'
  | 'DONE_WITH_ERRORS_WO_MATCH'
  | 'CANCELED'
  | 'SHOPIFY'
  | 'SHOPIFY_PROCESSING'
  | 'INITIATED'
  | 'FETCHED'
  | 'PARSED'
  | 'SAVED'
  | 'SKIPPED'
  | 'FAILED'

export type StatusContent = {
  message: string | JSX.Element
  shortMessage: string
  status: 'info' | 'success' | 'warning' | 'error'
  dismissible?: boolean
  finished?: boolean
}

const messages: Record<BatchImportStatus, StatusContent> = {
  PREPARING: {
    status: 'info',
    message:
      'Step: 1/3 - We received your file successfully. The file is under validation now.',
    shortMessage: '(1/3) Uploaded',
  },
  UPLOADED: {
    status: 'info',
    message:
      'Step: 1/3 - We received your file successfully. The file is under validation now.',
    shortMessage: '(1/3) Uploaded',
  },
  VALIDATED: {
    status: 'info',
    message: 'Step: 2/3 - Great Job. Now we start importing your products.',
    shortMessage: '(2/3) Validated',
  },

  VALIDATED_WITH_ERRORS: {
    status: 'info',
    message:
      'Step: 2/3 - We detected several issues in the file. We will only import products without issues.',
    shortMessage: '(2/3) Validated',
  },
  INVALID: {
    status: 'error',
    dismissible: true,
    finished: true,
    message:
      'This file is in incorrect format. Correct the file and Start Import again.',
    shortMessage: 'Invalid',
  },
  IMPORTED: {
    status: 'info',
    message:
      'Step: 3/3 - Importing process is complete. Your products will be visible on panel shortly.',
    shortMessage: '(3/3) Imported',
  },
  IMPORTED_WITH_ERRORS: {
    status: 'info',
    message:
      'Step: 3/3 - Importing process is complete. Your products will be visible on panel shortly.',
    shortMessage: '(3/3) Imported',
  },
  EANSEARCH: {
    status: 'success',
    dismissible: true,
    message:
      'Now fetching your competitor URLs. They will be automatically populated on your Products. Get started with a new Batch Import any time in this tab.',
    shortMessage: 'Searching for competitors',
  },
  EANSEARCH_WITH_ERRORS: {
    status: 'warning',
    dismissible: true,
    message:
      'Now fetching your competitor URLs. They will be automatically populated on your Products. Get started with a new Batch Import any time in this tab.',
    shortMessage: 'Searching for competitors',
  },
  DONE: {
    status: 'success',
    dismissible: true,
    finished: true,
    message: (
      <>
        Success. Batch Import is complete. To review your products, tap{' '}
        <Link to="/products?orderBy=date_created&orderDirection=desc">
          <u>here</u>
        </Link>
        .
      </>
    ),
    shortMessage: 'Done',
  },
  DONE_W_SMARTMATCH: {
    status: 'success',
    dismissible: true,
    finished: true,
    message: (
      <>
        Success. Batch Import and Competitor Matching is complete. To review
        your products, tap{' '}
        <Link to="/products?orderBy=date_created&orderDirection=desc">
          <u>here</u>
        </Link>
        .
      </>
    ),
    shortMessage: 'Done',
  },
  DONE_W_SMARTMATCH_NOMATCH: {
    status: 'warning',
    dismissible: true,
    finished: true,
    message: (
      <>
        Partial Success. Batch Import is successfully complete but we couldn\'t
        match any competitor URLs. To review your products, tap{' '}
        <Link to="/products?orderBy=date_created&orderDirection=desc">
          <u>here</u>
        </Link>
        .
      </>
    ),
    shortMessage: 'Done',
  },
  DONE_WITH_ERRORS: {
    status: 'warning',
    dismissible: true,
    finished: true,
    message: (
      <>
        Partial Success. Batch Import is partially successful. To review the
        imported products, tap{' '}
        <Link to="/products?orderBy=date_created&orderDirection=desc">
          <u>here</u>
        </Link>
        .
      </>
    ),
    shortMessage: 'Done',
  },
  DONE_WITH_CAVEATS: {
    status: 'success',
    dismissible: true,
    finished: true,
    message:
      'Success. Batch Import is complete with a few good to know caveats. Review the warning file in Actions below.',
    shortMessage: 'Done',
  },
  DONE_WITH_ERRORS_WO_MATCH: {
    status: 'warning',
    dismissible: true,
    finished: true,
    message: (
      <>
        Partial Success. Batch Import is partially successful. To review the
        imported products, tap{' '}
        <Link to="/products?orderBy=date_created&orderDirection=desc">
          <u>here</u>
        </Link>
        .
      </>
    ),
    shortMessage: 'Done',
  },
  CANCELED: {
    status: 'error',
    dismissible: true,
    finished: true,
    message: 'Import is cancelled. Please start again.',
    shortMessage: 'Canceled',
  },
  SHOPIFY: {
    status: 'info',
    message: 'We are processing your products.',
    shortMessage: 'Imported',
  },
  SHOPIFY_PROCESSING: {
    status: 'info',
    message: 'We are processing your products.',
    shortMessage: 'Imported',
  },
  INITIATED: {
    status: 'info',
    message: 'We started the process successfully. It is now being executed.',
    shortMessage: 'Initiated',
    dismissible: true,
    finished: false,
  },
  FETCHED: {
    status: 'info',
    message:
      'We fetched your feed. Depending on your file size, this may take a few minutes.',
    shortMessage: 'Fetching',
    finished: false,
  },
  PARSED: {
    status: 'info',
    message:
      'We validated your feed and are now syncing your products. This may take a few minutes.',
    shortMessage: 'Parsing',
    finished: false,
  },
  SAVED: {
    status: 'info',
    message: 'Your products are being updated. Almost done.',
    shortMessage: 'Saving',
    finished: false,
  },
  SKIPPED: {
    status: 'warning',
    message:
      'Sync skipped — your feed had no valid products. No changes were applied.',
    shortMessage: 'Skipped',
    dismissible: true,
    finished: true,
  },
  FAILED: {
    status: 'error',
    message:
      'Sync failed. Please review the error log and check your feed, then try again.',
    shortMessage: 'Failed',
    dismissible: true,
    finished: true,
  },
}

export function getBatchImportFullMessage(
  status: BatchImportStatus,
): StatusContent {
  return messages[status]
}

export function getBatchImportMessage(status: BatchImportStatus): string {
  return messages?.[status]?.shortMessage ?? ''
}
